import autobind from 'autobind-decorator';
import { markRaw } from '#imports';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import IMSdk from '@hetao/imsdk-new';
import { RouterService } from '@core/services/router.service';
import { StorageService } from '@core/services/storage.service';
import type { MsgVO } from '@core/types/im';
import { isProd } from '@core/utils/environment';
import {
  IM_CONFIG_APPID_DEV,
  IM_CONFIG_APPID_PROD,
  IM_CONFIG_APPKEY_DEV,
  IM_CONFIG_APPKEY_PROD,
} from './constant';
import { ImService } from './im.service';

@Injectable()
export class SdkService {
  // 融云sdk实例
  public sdkInstance = markRaw<any>({});

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => ImService))
  public imService!: ImService;

  public init() {
    const sdkConf = this.getConfig();
    const instance = new IMSdk(sdkConf, (event: any) => {
      console.log('埋点回调', event);
    });

    // 监听融云连接状态切换
    instance.on('connectStatusChange', this.handleConnectStatusChange);

    // 监听融云消息
    instance.on('imMsg', this.handleMessage);

    this.sdkInstance = markRaw(instance);
  }

  public destroy() {
    if (this.sdkInstance && this.sdkInstance.destroy) {
      this.sdkInstance.destroy();
    }
  }

  private getConfig() {
    return {
      appEnv: isProd() ? 'prod' : 'testing', // 融云初始化环境
      userId: this.imService.self.id, // 当前的用户 id
      type: this.imService.selfType, // 当前的 type，获取 token 的前缀 目前有 student 和 teacher
      appIds: {
        // 测试环境和正式环境的 appId，编程：uniq_channel  扩科：live-service
        dev: IM_CONFIG_APPID_DEV,
        prod: IM_CONFIG_APPID_PROD,
      },
      appKeys: {
        // 测试环境和正式环境的 appKey @李廷忠 @散宝峰 @刘关强
        dev: IM_CONFIG_APPKEY_DEV,
        prod: IM_CONFIG_APPKEY_PROD,
      },
    };
  }

  // 处理融云连接状态切换
  @autobind
  private handleConnectStatusChange(status: number) {
    if (status === 0) {
      console.log('融云连接成功，开始加入聊天室');
      const roomId = `STATUS_ROOM_${this.imService.peerId}`;
      this.sdkInstance.joinChatRoom(roomId);
    } else if (status === 1) {
      console.log('融云连接中…');
    } else if (status === 3) {
      console.log('融云连接断开');
    }
    this.imService.setSelfOnline(status === 0);
  }

  @autobind
  private handleMessage(data: MsgVO) {
    const parsed = this.parseMessage(data);
    // 屏蔽离线消息，不处理
    if (!parsed) return;
    // 继续业务逻辑处理
    const teacherId = this.imService.peerId;
    if (parsed.sender === teacherId || parsed.sender === 'system') {
      // 必须过滤来自当前会话的老师的消息
      this.imService.messageService.handleReceiveMessage(parsed);
    }
  }

  // 收到的融云消息经过 base64，需要解密处理
  private parseMessage(originMessage: any) {
    // 离线消息直接返回，不处理
    if (originMessage.offLineMessage || originMessage.isOffLineMessage) return;
    const msg = JSON.parse(originMessage.content.content);
    if (msg && msg.msgBody) {
      const data = {
        sender: this.decodeBase64(msg.sender),
        receivers: msg.receiverInfo.private.users.map((item: string) =>
          this.decodeBase64(item),
        ),
        msgBody: JSON.parse(this.decodeBase64(msg.msgBody)),
        msgId: msg.msgId,
        msgTime: msg.msgTime || originMessage.sentTime / 1000,
        readStatus: 0,
        offLineMessage: msg.offLineMessage || originMessage.isOffLineMessage,
      };
      return data;
    } else if (msg && msg.statusInfo) {
      this.imService.updateTeacherStatus(msg);
    }
  }

  private decodeBase64(base64: string) {
    return decodeURIComponent(escape(window.atob(base64)));
  }
}
